import { useState, useEffect, useRef, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceLine,
  BarChart,
  Bar,
} from "recharts";
import { useTheme } from "../context/ThemeContext";
import {
  ChevronDown,
  Info,
  Loader2,
  Download as DownloadIcon,
  Download,
  BarChart3,
  Activity,
} from "lucide-react";
import { get } from "../Libraries/Ajax/HttpService";
import { useDistrict } from "../utils/DistrictContext";
import moment from "moment";
import {
  DatePicker,
  Space,
  ConfigProvider,
  theme as antTheme,
  Spin,
} from "antd";
import { SwapOutlined } from "@ant-design/icons";
import { CSVLink } from "react-csv";
import * as htmlToImage from "dom-to-image-more";
import { toast } from "react-toastify";
import { makeDisabledDatePastNDays } from "../utils/dateLimits";
import InfoTooltip from "../components/ui/InfoTooltip";
import dayjs from "dayjs";
const { RangePicker } = DatePicker;

export default function FlowAnalytics({
  dateRange,
  setDateRange,
  interval,
  setInterval,
  system,
  setSystem,
  activeSubTab,
  setActiveSubTab,
  demandCategory,
  setDemandCategory,
}) {
  const { colors, theme } = useTheme();
  const domEl = useRef(null);

  // Safely extract string values in case the parent passed an array
  const resolveString = (val) => (Array.isArray(val) ? val[0] : val);
  const safeSystem = resolveString(system) || "total";
  const safeDemandCategory = resolveString(demandCategory) || "ALL";
  const safeDateRange = resolveString(dateRange) || "Past 30 Days";
  const safeInterval = resolveString(interval) || "1 Weeks";
  const [intervaltest, setIntervaltest] = useState("Days");
  const [sslvalue, setSslvalue] = useState(1);
  const requestIdRef = useRef(0);
  // Custom Date Range States
  const [parameterone, setParameterone] = useState(null);
  const [parametertwo, setParametertwo] = useState(null);

  // Download States
  const [csvData, setCsvData] = useState([]);
  const [isDownloadOpen, setIsDownloadOpen] = useState(false);
  const [showMaintenance, setShowMaintenance] = useState(false);

  // District Logic
  const selectedDistrict = useDistrict();
  const districtId = selectedDistrict?.selectedDistrict;
  const isFB151 = districtId === "FB151";
  const [chartType, setChartType] = useState("area"); // 'area' or 'bar'
  const dayDiff =
    parameterone && parametertwo
      ? dayjs(parametertwo).diff(dayjs(parameterone), "day")
      : 0;

  // Normalize system parameter
  const getSystemParam = (sys) => {
    const s = String(sys).toLowerCase().trim();
    if (s === "plant" || s === "total") return "total";
    if (s === "potable") return "potable";
    if (s === "non-potable" || s === "non_potable") return "non_potable";
    return "total";
  };

  useEffect(() => {
    if (safeDateRange === "Past Day") {
      setIntervaltest("Hours");
    } else if (
      ["Past Month", "Past 90 Days", "Past Year"].includes(safeDateRange)
    ) {
      setIntervaltest("Weeks");
    } else {
      setIntervaltest("Days");
    }
  }, [safeDateRange]);

  const resolvedSystem = getSystemParam(safeSystem);
  const currentSystemValue = getSystemParam(system);
  const OutlierDot = (props) => {
    const { cx, cy, payload } = props;

    if (payload.isOutlier) {
      return (
        <circle
          cx={cx}
          cy={cy}
          r={5}
          stroke="none"
          fill="#ef4444" // Tailwind red-500
          className="drop-shadow-sm"
        />
      );
    }

    return null; // Return nothing for normal points
  };
  const disabledDate = (current) => {
    // Can not select days after today
    return current && current > moment().endOf("day");
  };

  // Intake is ONLY available for the "total" (Plant) system, and never for FB151
  const availableTabs =
    isFB151 || resolvedSystem !== "total"
      ? ["accountability", "supply", "demand"]
      : ["accountability", "intake", "supply", "demand"];

  useEffect(() => {
    if ((isFB151 || resolvedSystem !== "total") && activeSubTab === "intake") {
      setActiveSubTab("accountability");
    }
  }, [isFB151, resolvedSystem, activeSubTab, setActiveSubTab]);

  useEffect(() => {
    if (isFB151 && resolvedSystem !== "total") {
      setSystem("total");
    }
  }, [isFB151, resolvedSystem, setSystem]);
  // Reset category when switching between Accountability, Intake, Supply, or Demand
  useEffect(() => {
    setDemandCategory("ALL");
  }, [districtId, resolvedSystem, activeSubTab, setDemandCategory]);

  useEffect(() => {
    // Logic specifically for FB151, Accountability/Supply, and Past Day
    const isSpecialFB151Case =
      isFB151 &&
      (activeSubTab === "accountability" || activeSubTab === "supply") &&
      safeDateRange === "Past Day";

    if (isSpecialFB151Case) {
      setIntervaltest("Days"); // Force 'Day' as default for this specific case
    } else if (safeDateRange === "Past Day") {
      setIntervaltest("Hours"); // Standard default for Past Day
    } else if (safeDateRange === "Past Year") {
      setIntervaltest("Months");
    } else if (["Past Month", "Past 90 Days"].includes(safeDateRange)) {
      setIntervaltest("Weeks");
    } else if (safeDateRange === "Custom") {
      if (dayDiff > 180) setIntervaltest("Months");
      else if (dayDiff > 30) setIntervaltest("Weeks");
      else setIntervaltest("Days");
    } else {
      setIntervaltest("Days");
    }
  }, [safeDateRange, parameterone, parametertwo, isFB151, activeSubTab]);

  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [chartKeys, setChartKeys] = useState(["value"]);
  const [apiStats, setApiStats] = useState({
    average: 0,
    maximum: 0,
    minimum: 0,
    total: 0,
    account_flow: [],
  });

  const getAccountabilityScale = () => {
    const maxValue = chartData.reduce(
      (max, item) =>
        Math.max(
          max,
          item.clampedValue || 0,
          item.accountability || 0,
          item.withMaintenance || 0,
        ),
      0,
    );

    const ceiling = Math.min(
      300,
      Math.ceil(Math.max(110, maxValue + 5) / 10) * 10,
    );

    const step = ceiling > 200 ? 50 : ceiling > 120 ? 20 : 5;
    const ticks = [];
    for (let i = 0; i <= ceiling; i += step) {
      ticks.push(i);
    }

    // Ensure the critical "100" mark is always visible
    if (!ticks.includes(100)) ticks.push(100);

    return { domain: [0, ceiling], ticks: ticks.sort((a, b) => a - b) };
  };
  const accScale = getAccountabilityScale();
  const getOptionParam = (range) => {
    switch (range) {
      case "Past Week":
        return "Past Week";
      case "Past Day":
        return "Past Day";
      case "Past 30 Days":
        return "Past Month";
      case "Past 90 Days":
        return "Past 3 Months";
      case "Past Year":
        return "Past Year";
      case "Custom":
        return "Custom";
      default:
        return "Past Month";
    }
  };

  const getDemandCategories = () => {
    if (isFB151 && resolvedSystem === "total") {
      return [
        { value: "ALL", label: "All" },
        { value: "DKRP0", label: "Residential" },
        { value: "DKCP0", label: "Commercial" },
        { value: "DKDP0", label: "District Facility" },
        { value: "DICP0", label: "Irrigation" },
        { value: "DIDP0", label: "Tracking" },
        { value: "DISP0", label: "School Irrigation" },
        { value: "DKSP0", label: "School" },
        { value: "DKHP0", label: "HOA Facility" },
      ];
    }
    if (resolvedSystem === "potable") {
      return [
        { value: "ALL", label: "All" },
        { value: "DICP1", label: "Potable Irrigation" },
        { value: "DKRP1", label: "Potable Domestic" },
        { value: "DFCP1", label: "Potable Fire" },
      ];
    }
    return [
      { value: "ALL", label: "All" },
      { value: "DICP1", label: "Potable Irrigation" },
      { value: "DKRP1", label: "Potable Domestic" },
      { value: "DFCP1", label: "Potable Fire" },
      { value: "NPS(P)", label: "Non-Potable Supply" },
      { value: "I/C", label: "Interconnect" },
    ];
  };

  const isValidCategory = (categoryValue) => {
  return getDemandCategories().some(
    (category) => category.value === categoryValue
  );
};

const effectiveDemandCategory = isValidCategory(safeDemandCategory)
  ? safeDemandCategory
  : "ALL";

  useEffect(() => {
    const controller = new AbortController(); // Initialize the controller
    const currentRequestId = ++requestIdRef.current; // Increment request ID
    setLoading(true); // Show loader

    const fetchFlowData = async () => {
      try {
        const optionParam = getOptionParam(safeDateRange);
        const rawInterval = String(safeInterval || "1 Weeks");
        const [numStr] = rawInterval.split(" ");
        const slvalue = numStr || "1";

        let endpoint = "accountability";
        let classCode = "ALL";

        if (activeSubTab === "accountability") {
          endpoint = "accountability";
        } else if (activeSubTab === "intake") {
          endpoint = "total_flow_produced";
        } else if (activeSubTab === "supply") {
          endpoint = "total_flow_supply";
          if (!isFB151 && resolvedSystem === "potable") {
            classCode =
              effectiveDemandCategory  !== "ALL" ? effectiveDemandCategory : "ALL";
          }
        } else if (activeSubTab === "demand") {
          endpoint = "total_flow_demand";
          classCode = effectiveDemandCategory;
        }

        let dateQuery = `option=${optionParam}`;
        if (safeDateRange === "Custom" && parameterone && parametertwo) {
          dateQuery += `&start_date=${parameterone}&end_date=${parametertwo}`;
        }

        // Fetch CSV Data
        get(
          `apis/download/allflowanalytics?${dateQuery}&interval=${intervaltest}&number=${sslvalue}&system=${resolvedSystem}&class_code=${classCode}`,
          { signal: controller.signal },
        ).then((res) => {
          if (res?.data) {
            console.log("CSV API Data: ", res.data); // Log the API data
            setCsvData(res.data);
          } else {
            console.error("Failed to fetch CSV data");
          }
        });

        // Fetch Chart Data

        const res = await get(
          `apis/${endpoint}?${dateQuery}&interval=${intervaltest}&number=${sslvalue}&system=${resolvedSystem}&class_code=${classCode}`,
          { signal: controller.signal }, // Pass the signal to the GET request
        );

        // Ignore outdated responses
        if (currentRequestId !== requestIdRef.current) return;

        const apiResponse = res?.data?.data || {};

        const base = apiResponse.accountability_data || {};
        const maintTemp =
          apiResponse.maintenance_temp_meter_accountability_data || {};
        const combined =
          apiResponse.accountability_with_maintainance_and_temp_meter_data ||
          {};

        setApiStats({
          // 1. Basic Accountability
          average: base.average || 0,
          maximum: base.maximum || 0,
          minimum: base.minimum || 0,
          total: apiResponse.total || 0,
          demand_total: base.demand_total || 0,
          supply_total: base.supply_total || 0,
          accountability_percentage: base.accountability_percentage || 0,
          account_flow: apiResponse.account_flow || [],

          // 2. Maint + Temp
          average_maint_temp: maintTemp.average_maintenance_temp_meter || 0,
          maximum_maint_temp: maintTemp.maximum_maintenance_temp_meter || 0,
          minimum_maint_temp: maintTemp.minimum_maintenance_temp_meter || 0,
          demand_total_maint_temp:
            maintTemp.demand_total_maintenance_temp_meter || 0,

          // 3. Total (Acct + Maint + Temp)
          average_total: combined.average_with_maintainance_and_temp_meter || 0,
          maximum_total: combined.maximum_with_maintainance_and_temp_meter || 0,
          minimum_total: combined.minimum_with_maintainance_and_temp_meter || 0,
          demand_total_total:
            combined.demand_total_with_maintainance_and_temp_meter || 0,
          percentage_total:
            combined.accountability_percentage_with_maintainance_and_temp_meter ||
            0,
        });

        const newChartData = [];
        const keys = new Set();

        if (apiResponse.total_flow && apiResponse.total_flow.length === 0) {
          // No data available for the selected date range
          setChartData([]); // Empty chart data
          setChartKeys([]); // Empty chart keys
          setLoading(false); // Hide loader
          return; // Exit the function
        }

        if (activeSubTab === "accountability" && base.accountability) {
          const baseArr = base.accountability || [];
          const combinedArr =
            combined.accountability_with_maintainance_and_temp_meter || [];
          const maintArr =
            apiResponse.maintenance_accountability_data
              ?.maintenance_accountability || [];
          const tempArr =
            apiResponse.temp_meter_accountability_data
              ?.temp_meter_accountability || [];

          baseArr.forEach((item, idx) => {
            const combinedItem = combinedArr[idx] || {};
            const maintItem = maintArr[idx] || {};
            const tempItem = tempArr[idx] || {};

            newChartData.push({
              date: item.option,
              // --- SWAP LOGIC HERE ---
              // Primary Line (Label: Accountability) now uses Combined Data
              accountability: Number(combinedItem.accountability || 0),
              // Secondary Line (Label: Acct - Maint) now uses Base Data
              totalAcc: Number(item.accountability || 0),

              // Maintenance and Temp lines
              maintenance: Number(maintItem.accountability || 0),
              tempMeter: Number(tempItem.accountability || 0),

              // Tooltip breakdown values
              demandFlow_base: item.demand_flow || 0,
              demandFlow_total: combinedItem.demand_flow || 0,
              demandFlow_maint: maintItem.demand_flow || 0,
              demandFlow_temp: tempItem.demand_flow || 0,
              supplyFlow: item.supply_flow || 0,
              isOutlier: item.outliers === 1,
            });
          });
        } else if (Array.isArray(apiResponse.total_flow)) {
          apiResponse.total_flow.forEach((arr) => {
            if (Array.isArray(arr) && arr.length > 0) {
              let entry = { date: arr[0].flow_date };
              arr.forEach((item) => {
                const key =
                  isFB151 && item.code_name
                    ? item.code_name.trim()
                    : item.account_name?.trim() ||
                      item.code_name?.trim() ||
                      "value";

                entry[key] = item.flow;
                keys.add(key);
              });
              newChartData.push(entry);
            }
          });
        }

        // If chart data exists, set it
        if (newChartData.length > 0) {
          setChartData(newChartData);
          setChartKeys(Array.from(keys));
        } else {
          console.warn("No valid chart data found.");
          setChartData([]); // Clear chart data if no valid data
          setChartKeys([]); // Clear chart keys if no valid data
        }
      } catch (err) {
        if (err.code !== "ERR_CANCELED") {
          console.error("Failed to fetch analytics:", err);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setLoading(false); // Hide loader
        }
      }
    };

    fetchFlowData();

    return () => controller.abort(); // Abort the request if component unmounts or request is outdated
  }, [
    safeDateRange,
    parameterone,
    parametertwo,
    safeInterval,
    intervaltest,
    resolvedSystem,
    activeSubTab,
    effectiveDemandCategory,
    isFB151,
    districtId,
    sslvalue,
  ]);

  // Handlers
  const onRangeChange = (dates, dateStrings) => {
    if (dateStrings) {
      setParameterone(dateStrings[0]);
      setParametertwo(dateStrings[1]);
    } else {
      setParameterone(null);
      setParametertwo(null);
    }
  };

  const downloadImage = async () => {
    const el = domEl.current;
    if (!el) {
      toast.error("Target element not found");
      return;
    }

    try {
      // ✅ Fix radio / checkbox state for dom-to-image
      const radios = el.querySelectorAll('input[type="radio"]');
      radios.forEach((radio) => {
        if (radio.checked) {
          radio.setAttribute("checked", "checked");
        } else {
          radio.removeAttribute("checked");
        }
      });

      const rect = el.getBoundingClientRect();
      const width = Math.ceil(rect.width);
      const height = Math.ceil(rect.height);

      const dataUrl = await htmlToImage.toPng(el, {
        width,
        height,
        cacheBust: true,
        backgroundColor: colors.cardBg || "white",
        style: {
          width: `${width}px`,
          height: `${height}px`,
          overflow: "visible",
          transform: "none",
          display: "block",
        },
      });

      const link = document.createElement("a");
      link.download = `FlowAnalytics-${activeSubTab}-${safeDateRange}-${districtId || "Export"}.png`;
      link.href = dataUrl;
      link.click();

      setIsDownloadOpen(false);
    } catch (err) {
      console.error("Failed to export image:", err);
      toast.error("Failed to download image.");
    }
  };
  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const dataPoint = payload[0].payload;
    const isAccountability = activeSubTab === "accountability";

    // 1. Calculate the total for this specific date (to determine percentages)
    const totalForDate = payload.reduce(
      (sum, entry) => sum + (Number(entry.value) || 0),
      0,
    );

    // 2. Filter and Sort the payload
    let sortedPayload = [...payload];

    if (isAccountability) {
      sortedPayload = sortedPayload.filter((entry) => {
        // 1. Remove the outlier line by name
        if (entry.name === "outlier") return false;

        // 2. Remove internal scaling key
        if (entry.dataKey === "clampedValue") return false;

        // 3. Prevent duplicate Accountability lines from Area/Line mode
        // If we are in Bar mode, we only want the "Bar" entry
        if (
          chartType === "bar" &&
          entry.type === "monotone" &&
          entry.dataKey === "accountability"
        ) {
          return false;
        }

        return true;
      });

      // Final deduplication by name just in case
      sortedPayload = sortedPayload.filter(
        (entry, index, self) =>
          index === self.findIndex((t) => t.name === entry.name),
      );
    }
    // Sort by value descending for better readability
    sortedPayload.sort(
      (a, b) => (Number(b.value) || 0) - (Number(a.value) || 0),
    );

    return (
      <div
        className="rounded-lg shadow-xl px-4 py-3 border max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-500"
        onWheel={(e) => e.stopPropagation()}
        style={{
          backgroundColor: theme === "white" ? "#ffffff" : colors.background,
          borderColor: `${colors.primary}30`,
          boxShadow: `0 8px 32px ${colors.primary}30`,
          backdropFilter: "blur(6px)",
          pointerEvents: "auto",
        }}
      >
        <div
          className="flex justify-between items-center gap-6 mb-2 pb-2 border-b"
          style={{ borderColor: colors.border, background: colors.background }}
        >
          <span className="text-xs" style={{ color: colors.textSecondary }}>
            {dataPoint.date}
          </span>

          {!isAccountability && totalForDate > 0 && (
            <span className="text-xs font-bold" style={{ color: colors.text }}>
              Total: {Math.round(totalForDate).toLocaleString("en-US")}
            </span>
          )}
        </div>

        <div className="space-y-1">
          {sortedPayload.map((entry, index) => {
            // Resolve the value to display
            let displayValue = entry.value;

            // Logic for specific keys
            if (entry.dataKey === "totalAcc") {
              // 'totalAcc' is your "Acct - Maint" (Base data)
              displayValue = dataPoint.totalAcc;
            } else if (entry.dataKey === "accountability") {
              // 'accountability' is your combined data
              displayValue = dataPoint.accountability;
            }

            const percentage =
              totalForDate > 0
                ? ((Number(entry.value) / totalForDate) * 100).toFixed(1)
                : "0.0";

            // Format names
            let displayName = entry.name;
            if (!displayName || displayName === "value") displayName = "Total";
            if (displayName === "withMaintenance") displayName = "Acct - Maint";
            if (displayName === "totalAcc") displayName = "Acct - Maint";
            if (displayName === "maintenance") displayName = "Maintenance";

            // Color logic
            const isWithMaintenance =
              entry.dataKey === "withMaintenance" ||
              entry.dataKey === "totalAcc";
            const isTransparent =
              entry.color === "transparent" || entry.color === "none";

            let textColor = isTransparent
              ? colors.text
              : entry.color || colors.text;
            let dotColor = isTransparent ? "#10b981" : entry.color;

            if (isWithMaintenance) {
              textColor = "#10b981";
              dotColor = "#10b981";
            }

            return (
              <div
                key={index}
                className="flex justify-between gap-6 items-center"
              >
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: dotColor }}
                  />
                  <span
                    className="text-[14px] font-medium"
                    style={{ color: textColor }}
                  >
                    {displayName}
                  </span>
                </div>
                <span
                  className="font-bold text-sm tabular-nums"
                  style={{ color: colors.text }}
                >
                  {Math.round(Number(displayValue)).toLocaleString()}
                  {isAccountability ? "%" : ` (${percentage}%)`}
                </span>
              </div>
            );
          })}

          {isAccountability && (
            <div
              className="mt-2 pt-2 border-t space-y-1"
              style={{ borderColor: colors.border }}
            >
              <div className="flex justify-between gap-8 text-[11px]">
                <span style={{ color: colors.textSecondary }}>Demand:</span>
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: colors.text }}
                >
                  {Math.round(dataPoint.demandFlow_base || 0).toLocaleString()}
                </span>
              </div>
              {isFB151 && (
                <>
                  <div className="flex justify-between gap-8 text-[11px]">
                    <span style={{ color: colors.textSecondary }}>
                      Maintenance:
                    </span>
                    <span
                      className="font-semibold tabular-nums"
                      style={{ color: colors.text }}
                    >
                      {Math.round(
                        dataPoint.demandFlow_maint || 0,
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between gap-8 text-[11px]">
                    <span style={{ color: colors.textSecondary }}>
                      Total Demand:
                    </span>
                    <span
                      className="font-semibold tabular-nums"
                      style={{ color: colors.text }}
                    >
                      {Math.round(
                        dataPoint.demandFlow_total || 0,
                      ).toLocaleString()}
                    </span>
                  </div>
                </>
              )}
              <div
                className="flex justify-between gap-8 text-[11px] pt-1 mt-1 border-t border-dashed"
                style={{ borderColor: colors.border }}
              >
                <span style={{ color: colors.textSecondary }}>
                  Supply Flow:
                </span>
                <span
                  className="font-semibold tabular-nums"
                  style={{ color: colors.text }}
                >
                  {Math.round(dataPoint.supplyFlow || 0).toLocaleString()}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };
  // const areaColors = [
  //   colors.primary,
  //   "#10b981",
  //   "#f97316",
  //   "#8b5cf6",
  //   "#ec4899",
  //   "#06b6d4",
  //   "#eab308",
  // ];
  // Replace the static areaColors array with this useMemo
  const areaColors = useMemo(() => {
    // If system is non-potable, lead with purple shades
    if (resolvedSystem === "non_potable") {
      return [
        "#A855F7", // Main Purple
        "#C084FC", // Light Purple
        "#7E22CE", // Deep Purple
        "#D8B4FE",
        "#9333EA",
      ];
    }
    // Default palette for Potable / Plant
    return [
      colors.primary,
      "#10b981",
      "#f97316",
      "#8b5cf6",
      "#ec4899",
      "#06b6d4",
      "#eab308",
    ];
  }, [resolvedSystem, colors.primary]);

  return (
    <>
      {/* Filter Controls and Sub Tabs Row */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-4 mb-6">
        {/* Filter Controls - Left Side */}
        <div className="flex flex-wrap items-end gap-4 lg:gap-6 w-full lg:w-auto">
          <div className="flex flex-wrap items-end gap-4 w-full lg:w-auto">
            {/* Unified Configuration Bar */}
            <div className="flex-1">
              <label
                className="text-xs mb-2 block font-semibold"
                style={{ color: colors.primary }}
              >
                Range | Interval | Custom
              </label>
              <div
                className="flex items-center gap-1 p-1 rounded-xl border w-full max-[992px]:flex-wrap"
                style={{
                  backgroundColor: colors.background,
                  borderColor: colors.border,
                }}
              >
                {/* SECTION 1: RANGE PRESETS */}
                <div className="flex items-center gap-1">
                  {[
                    { label: "D", value: "Past Day" },
                    { label: "W", value: "Past Week" },
                    { label: "1M", value: "Past 30 Days" },
                    { label: "3M", value: "Past 90 Days" },
                    { label: "YTD", value: "YTD" }, // Added YTD
                    { label: "1Y", value: "Past Year" },
                  ].map((tp) => {
                    // Logic to identify if YTD is the active state
                    const isYTDActive =
                      tp.value === "YTD" &&
                      parameterone ===
                        dayjs().startOf("year").format("YYYY-MM-DD") &&
                      parametertwo === dayjs().format("YYYY-MM-DD");

                    const isActive = safeDateRange === tp.value || isYTDActive;

                    return (
                      <button
                        key={tp.value}
                        onClick={() => {
                          if (tp.value === "YTD") {
                            // Calculate Jan 1st to Today
                            const startOfYear = dayjs()
                              .startOf("year")
                              .format("YYYY-MM-DD");
                            const today = dayjs().format("YYYY-MM-DD");
                            setParameterone(startOfYear);
                            setParametertwo(today);
                            setDateRange("Custom"); // Set to Custom so the API uses start/end params
                            setIntervaltest("Weeks");
                          } else {
                            setDateRange(tp.value);
                            setParameterone(null);
                            setParametertwo(null);
                          }
                        }}
                        className="px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer"
                        style={{
                          backgroundColor: isActive
                            ? `${colors.primary}20`
                            : "transparent",
                          color: isActive
                            ? colors.primary
                            : colors.textSecondary,
                          border: isActive
                            ? `1px solid ${colors.primary}40`
                            : "1px solid transparent",
                        }}
                      >
                        {tp.label}
                      </button>
                    );
                  })}
                </div>

                <div
                  className="h-6 w-[1px] mx-1"
                  style={{ backgroundColor: colors.border }}
                />
                {/* SECTION 4: CUSTOM CALENDAR */}
                <ConfigProvider
                  theme={{
                    algorithm:
                      theme === "white"
                        ? antTheme.defaultAlgorithm
                        : antTheme.darkAlgorithm,
                    token: {
                      colorBgContainer: colors.background,
                      colorBorder: "transparent",
                      controlHeight: 32,
                    },
                  }}
                >
                  <RangePicker
                    value={[
                      parameterone ? dayjs(parameterone, "YYYY-MM-DD") : null,
                      parametertwo ? dayjs(parametertwo, "YYYY-MM-DD") : null,
                    ]}
                    onChange={(dates, dateStrings) => {
                      if (dateStrings[0] && dateStrings[1]) {
                        setParameterone(dateStrings[0]);
                        setParametertwo(dateStrings[1]);
                        setDateRange("Custom");
                      }
                    }}
                    disabledDate={(current) =>
                      current && current > dayjs().endOf("day")
                    }
                    variant="borderless"
                    placeholder={["Start", "End"]}
                    style={{ width: "250px" }}
                    classNames={{
                      popup: {
                        root: `mizu-calendar-popup calendar-${theme}`,
                      },
                    }}
                  />
                </ConfigProvider>
                <div
                  className="h-6 w-[1px] mx-1"
                  style={{ backgroundColor: colors.border }}
                />
                {/* SECTION 2: INTERVAL SELECTOR */}
                <div className="flex items-center gap-1">
                  {["Hour", "Day", "Week", "Month"].map((intvl) => {
                    const displayMap = {
                      Hour: "Hours",
                      Day: "Days",
                      Week: "Weeks",
                      Month: "Months",
                    };
                    const val = displayMap[intvl];
                    if (
                      intvl === "Hour" &&
                      isFB151 &&
                      (activeSubTab === "accountability" ||
                        activeSubTab === "supply")
                    ) {
                      return null;
                    }
                    if (
                      intvl === "Day" &&
                      safeDateRange === "Past Day" &&
                      !isFB151
                    ) {
                      return null;
                    }
                    if (
                      intvl === "Week" &&
                      ["Past Day", "Past Week"].includes(safeDateRange)
                    )
                      return null;
                    if (intvl === "Month") {
                      const isLargeCustomRange =
                        safeDateRange === "Custom" && dayDiff > 180;
                      if (safeDateRange !== "Past Year" && !isLargeCustomRange)
                        return null;
                    }

                    return (
                      <button
                        key={intvl}
                        onClick={() => setIntervaltest(val)}
                        className="px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all cursor-pointer"
                        style={{
                          backgroundColor:
                            intervaltest === val
                              ? colors.primary
                              : "transparent",
                          color:
                            intervaltest === val
                              ? "#fff"
                              : colors.textSecondary,
                        }}
                      >
                        {intvl}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          {/* System - Hidden if District is FB151 */}
          {!isFB151 && (
            <div>
              <label
                className="block text-xs mb-2 font-semibold"
                style={{ color: colors.primary }}
              >
                System
              </label>
              <div className="relative">
                <select
                  // value={resolvedSystem}
                  value={currentSystemValue}
                  onChange={(e) => setSystem(e.target.value)}
                  // className="w-full px-4 py-2 pr-10 border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none transition-all min-w-37.5"
                  // style={{
                  //   backgroundColor: colors.cardBg,
                  //   borderColor: colors.border,
                  //   color: colors.textSecondary,
                  // }}
                  className="w-full px-3 py-2 pr-8 border rounded-lg text-sm appearance-none cursor-pointer focus:outline-none transition-all"
                  style={{
                    background: colors.background,
                    borderColor: colors.border,
                    color: colors.text,
                  }}
                >
                  <option
                    value="total"
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      color: colors.text,
                    }}
                  >
                    Potable Plant
                  </option>
                  <option
                    value="potable"
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      color: colors.text,
                    }}
                  >
                    Potable
                  </option>
                  <option
                    value="non_potable"
                    style={{
                      backgroundColor:
                        theme === "white" ? "#ffffff" : colors.background,
                      color: colors.text,
                    }}
                  >
                    Non-Potable Plant
                  </option>
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                  style={{ color: colors.textSecondary }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Sub Tabs & Download - Right Side */}
        <div className="flex flex-wrap items-center gap-1 sm:gap-1 w-full lg:w-auto">
          {availableTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveSubTab(tab)}
              className="px-4 sm:px-5 py-2 text-sm font-semibold transition-all"
              style={{
                color:
                  activeSubTab === tab ? colors.primary : colors.textSecondary,
                borderBottom:
                  activeSubTab === tab ? `2px solid ${colors.primary}` : "none",
              }}
            >
              {tab === "accountability" ? (
                <div className="flex items-center gap-1">
                  Accountability
                  <InfoTooltip text="(Demand/Supply)*100" />
                </div>
              ) : (
                tab.charAt(0).toUpperCase() + tab.slice(1)
              )}
            </button>
          ))}
          {/* Maintenance Toggle - Only show on Accountability Tab for FB151 */}
          {activeSubTab === "accountability" && (
            // isFB151 && (
            <div
              className="flex items-center gap-2 p-1 rounded-lg border mr-2"
              style={{
                backgroundColor: colors.background,
                borderColor: colors.border,
              }}
            >
              <button
                onClick={() => setShowMaintenance(false)}
                className="px-3 py-1 rounded-md text-[11px] font-bold transition-all"
                style={{
                  backgroundColor: !showMaintenance
                    ? colors.primary
                    : "transparent",
                  color: !showMaintenance ? "#fff" : colors.textSecondary,
                }}
              >
                Basic
              </button>
              <button
                onClick={() => setShowMaintenance(true)}
                className="px-3 py-1 rounded-md text-[11px] font-bold transition-all"
                style={{
                  backgroundColor: showMaintenance
                    ? colors.primary
                    : "transparent",
                  color: showMaintenance ? "#fff" : colors.textSecondary,
                }}
              >
                Maintenance
              </button>
            </div>
          )}
          <div className="relative ml-auto">
            <button
              onClick={() => setIsDownloadOpen(!isDownloadOpen)}
              className="self-end p-2.5 rounded-lg  transition-all duration-300"
              style={{
                background: `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                color: colors.buttonText,
              }}
              title="Download Data"
            >
              <Download className="w-5 h-5 text-white" />
            </button>

            {isDownloadOpen && (
              <>
                {/* Overlay to close on click-outside */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setIsDownloadOpen(false)}
                ></div>

                {/* The Dropdown Menu */}
                <div
                  className="absolute right-0 mt-2 w-48 rounded-lg shadow-xl z-50 border overflow-hidden"
                  style={{
                    background: colors.background,
                    borderColor: colors.border,
                  }}
                >
                  <button
                    onClick={downloadImage}
                    className="w-full text-left px-4 py-3 text-sm transition-colors"
                    style={{ color: colors.text }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = colors.hoverBg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    Download as PNG
                  </button>

                  <div
                    className="border-b"
                    style={{ borderColor: colors.border }}
                  />

                  <CSVLink
                    data={csvData}
                    filename={`FlowAnalytics-${activeSubTab}-${safeDateRange}-${districtId || "Data"}.csv`}
                    className="block w-full text-left px-4 py-3 text-sm transition-colors"
                    style={{
                      textDecoration: "none",
                      color: colors.text,
                    }}
                    onClick={() => setIsDownloadOpen(false)}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.background = colors.hoverBg)
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.background = "transparent")
                    }
                  >
                    Download as CSV
                  </CSVLink>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Chart and Stats Container */}
      <div
        className="relative min-h-[550px] min-[1921px]:min-h-[1000px] mb-8"
        ref={domEl}
      >
        {/* Loader Overlay */}
        {/* {loading && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg">
            <Spin size="large" />
          </div>
        )} */}

        <div
          className={`flex flex-col lg:flex-row gap-4 lg:gap-6 transition-all duration-300 h-full ${
            loading ? "opacity-30 pointer-events-none" : "opacity-100"
          }`}
        >
          {/* Chart Section */}
          <div className="max-[767px]:overflow-x-scroll max-[992px]:w-full h-full w-[80%]  max-[1650px]:w-[76%] max-[1220px]:w-[70%] max-[1024px]:w-[100%]">
            <div
              className="w-full lg:flex-[8] h-[600px] max-[767px]:h-[400px] min-[1921px]:h-[1000px] backdrop-blur-sm border rounded-lg p-4 md:p-6 overflow-hidden dark:bg-transparent max-[767px]:w-[500px]"
              style={{
                backgroundColor: colors.cardBg,
                borderColor: colors.border,
              }}
            >
              {/* {activeSubTab !== "accountability" && ( */}
              <div className="absolute top-4 right-6 z-10 export-ignore">
                <button
                  onClick={() =>
                    setChartType(chartType === "area" ? "bar" : "area")
                  }
                  className="flex items-center justify-center w-9 h-9 rounded-lg border transition-all hover:bg-white/10"
                  style={{
                    backgroundColor: colors.background,
                    borderColor: colors.border,
                    color: colors.primary,
                  }}
                  title={
                    chartType === "area"
                      ? "Switch to Stacked Bar Chart"
                      : "Switch to Area Chart"
                  }
                >
                  {chartType === "area" ? (
                    <BarChart3 size={18} />
                  ) : (
                    <Activity size={18} />
                  )}
                </button>
              </div>
              {/* )} */}
              <ResponsiveContainer width="100%" height="100%">
                {loading ? (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ color: colors.textSecondary }}
                  >
                    <Spin size="large" />
                  </div>
                ) : chartData.length === 0 ? (
                  <div
                    className="w-full h-full flex items-center justify-center"
                    style={{ color: colors.textSecondary }}
                  >
                    No Data Available
                  </div>
                ) : (
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                  >
                    <defs>
                      <linearGradient
                        id="colorBlueGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={colors.primary}
                          stopOpacity={0.6}
                        />
                        <stop
                          offset="100%"
                          stopColor={colors.primary}
                          stopOpacity={0.1}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={colors.border}
                      vertical={false}
                    />

                    <XAxis
                      dataKey="date"
                      stroke={colors.textSecondary}
                      tickFormatter={(value) => {
                        if (!value) return "";

                        const date = moment(value);

                        if (!date.isValid()) {
                          return value;
                        }

                        if (intervaltest === "Months") {
                          return date.format("MMM YYYY"); // e.g., "Mar 2025"
                        }
                        return date.format("MMM DD, YYYY"); // e.g., "Mar 01, 2025"
                      }}
                      tick={{ fontSize: 10 }}
                    />

                    <YAxis
                      width={90}
                      label={{
                        value:
                          activeSubTab === "accountability"
                            ? "Plant Demand / Plant Supply %"
                            : "Gallons",
                        angle: -90,
                        position: "insideLeft",
                        fill: colors.textSecondary,
                        fontSize: 12,
                        style: { textAnchor: "middle" },
                      }}
                      tick={{ fill: colors.textSecondary, fontSize: 11 }}
                      stroke={colors.textSecondary}
                      tickFormatter={(value) => value.toLocaleString("en-US")}
                      domain={
                        activeSubTab === "accountability"
                          ? accScale.domain
                          : ["auto", "auto"]
                      }
                      ticks={
                        activeSubTab === "accountability"
                          ? accScale.ticks
                          : undefined
                      }
                      interval={0}
                    />

                    <Tooltip
                      content={<CustomTooltip />}
                      cursor={{
                        stroke: colors.border,
                        strokeWidth: 1,
                        strokeDasharray: "4 4",
                      }}
                      wrapperStyle={{
                        pointerEvents: "auto",
                      }}
                    />

                    {/* Important Fix: Recharts directly accepts these Reference components now (no Fragments used) */}
                    {activeSubTab === "accountability" ? (
                      <ReferenceArea
                        y1={0}
                        y2={85}
                        stroke="none"
                        fill="#f97316"
                        fillOpacity={0.3}
                      />
                    ) : null}
                    {activeSubTab === "accountability" ? (
                      <ReferenceArea
                        y1={85}
                        y2={95}
                        stroke="none"
                        fill="#fbbf24"
                        fillOpacity={0.3}
                      />
                    ) : null}
                    {activeSubTab === "accountability" ? (
                      <ReferenceArea
                        y1={95}
                        y2={100}
                        stroke="none"
                        fill="#10b981"
                        fillOpacity={0.3}
                      />
                    ) : null}

                    {activeSubTab === "accountability" &&
                    apiStats.average > 0 ? (
                      <ReferenceLine
                        y={apiStats.average}
                        stroke={colors.primary}
                        strokeDasharray="5 5"
                        label={({ viewBox }) => {
                          const { x, y, width } = viewBox;

                          return (
                            <text
                              x={x + width - 0} // push outside right edge
                              y={y - 0}
                              fill={colors.primary}
                              fontSize={12}
                              fontWeight="bold"
                              textAnchor="start"
                              dominantBaseline="middle"
                            >
                              Avg
                            </text>
                          );
                        }}
                      />
                    ) : null}

                    {/* Accountability Area (Lines) */}
                    {activeSubTab === "accountability" &&
                      chartType === "area" && (
                        // <Line
                        //   type="monotone"
                        //   dataKey={isFB151 ? "accountability" : "clampedValue"}
                        //   stroke={colors.primary}
                        //   strokeWidth={3}
                        //   dot={<OutlierDot />}
                        // />
                        <Line
                          type="monotone"
                          dataKey="accountability"
                          name="Accountability"
                          stroke={colors.primary} // This is your Blue
                          strokeWidth={4} // Made slightly thicker to stand out
                          dot={<OutlierDot />}
                          activeDot={{ r: 6 }}
                          connectNulls
                        />
                      )}
                    {activeSubTab === "accountability" &&
                      chartType === "area" &&
                      // isFB151 &&
                      showMaintenance && (
                        <Line
                          type="monotone"
                          dataKey="maintenance"
                          stroke="#f97316"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                        />
                      )}
                    {/* {activeSubTab === "accountability" &&
                      chartType === "area" &&
                      // isFB151 &&
                      showMaintenance && (
                        <Line
                          type="monotone"
                          dataKey="tempMeter"
                          name="tempMeter"
                          stroke="#8b5cf6"
                          strokeWidth={2}
                          strokeDasharray="3 3"
                          dot={false}
                        />
                      )} */}
                    {activeSubTab === "accountability" &&
                      chartType === "area" &&
                      // isFB151 &&
                      showMaintenance && (
                        <Line
                          type="monotone"
                          dataKey="totalAcc"
                          name="withMaintenance"
                          stroke="#10b981"
                          strokeWidth={3}
                          dot={false}
                        />
                      )}

                    {activeSubTab === "accountability" &&
                      chartType === "bar" && (
                        <Bar
                          dataKey="accountability"
                          fill={colors.primary}
                          stackId="stack1"
                          barSize={20}
                          name="Accountability"
                        />
                      )}

                    {/* Maintenance Bars (Stacked on top of Base) */}
                    {activeSubTab === "accountability" &&
                      chartType === "bar" &&
                      showMaintenance && (
                        <Bar
                          dataKey="maintenance"
                          fill="#f97316"
                          stackId="stack1"
                          barSize={20}
                          name="Maintenance"
                        />
                      )}

                    {activeSubTab === "accountability" &&
                      chartType === "bar" &&
                      showMaintenance && (
                        <Line
                          type="monotone"
                          dataKey="totalAcc"
                          stroke="none"
                          dot={false}
                          activeDot={false}
                          legendType="none"
                        />
                      )}

                    {/* Non-Accountability Area/Bars (Your existing map logic) */}
                    {activeSubTab !== "accountability" &&
                      chartKeys.map((key, index) => {
                        const isNonPotable = resolvedSystem === "non_potable";
                        const color = areaColors[index % areaColors.length];
                        return chartType === "area" ? (
                          <Area
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={color}
                            strokeWidth={2}
                            fill={
                              index === 0 &&
                              chartKeys.length === 1 &&
                              !isNonPotable
                                ? "url(#colorBlueGradient)"
                                : color
                            }
                            fillOpacity={0.85}
                            stackId="1"
                          />
                        ) : (
                          <Bar
                            key={key}
                            dataKey={key}
                            fill={color}
                            stackId="1"
                            radius={[0, 0, 0, 0]}
                            barSize={sslvalue > 4 ? 20 : 40}
                          />
                        );
                      })}

                    {/* Outlier line for bar chart view */}
                    {activeSubTab === "accountability" &&
                      chartType === "bar" && (
                        <Line
                          type="monotone"
                          dataKey="accountability"
                          name="outlier"
                          stroke="transparent"
                          dot={<OutlierDot />}
                          activeDot={false}
                          legendType="none"
                          tooltipType="none"
                        />
                      )}
                  </ComposedChart>
                )}
              </ResponsiveContainer>
            </div>
          </div>

          {/* Statistics Panel */}
          <div
            className="min-[1024px]:max-[1921px]:h-[600px] max-[1024px]:w-full w-[450px] lg:flex-[2] backdrop-blur-sm border rounded-lg p-6 space-y-6 max-[1920px]:h-full min-[1921px]:h-[1000px] overflow-y-scroll"
            style={{
              backgroundColor: colors.cardBg,
              borderColor: colors.border,
            }}
          >
            {resolvedSystem !== "non_potable" && (
              <>
                {activeSubTab === "demand" && (
                  <div className="mb-6">
                    <h3
                      className="font-bold text-sm mb-3"
                      style={{ color: colors.text }}
                    >
                      Categories
                    </h3>
                    <div className="space-y-2">
                      {getDemandCategories().map((category) => (
                        <label
                          key={category.value}
                          className="flex items-center gap-1 cursor-pointer border-none"
                          for={`category_${category.value}`}
                        >
                          <input
                            type="radio"
                            name="demandCategory"
                            value={category.value}
                            id={`category_${category.value}`}
                            checked={effectiveDemandCategory  === category.value}
                            onChange={() => setDemandCategory(category.value)}
                            className="w-4 h-4 focus:ring-2 outline-none border-none"
                            style={{
                              accentColor: colors.primary,
                              border: "none",
                              boxShadow: "none",
                            }}
                          />
                          <span
                            className="text-sm transition-colors w-full"
                            style={{ color: colors.textSecondary }}
                          >
                            {category.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {/* Supply tab sub-categories specifically for Potable (Non-FB151) */}
                {activeSubTab === "supply" &&
                  !isFB151 &&
                  resolvedSystem === "potable" && (
                    <div className="mb-6">
                      <h3
                        className="font-bold text-sm mb-3"
                        style={{ color: colors.text }}
                      >
                        Categories
                      </h3>
                      <div className="space-y-2">
                        {[
                          { value: "ALL", label: "All" },
                          { value: "DDOP1", label: "Distribution" },
                          { value: "DXOP1", label: "Interconnect (In)" },
                        ].map((category) => (
                          <label
                            key={category.value}
                            className="flex items-center gap-2 cursor-pointer group"
                          >
                            <input
                              type="radio"
                              name="supplyCategory"
                              value={category.value}
                              checked={effectiveDemandCategory === category.value}
                              onChange={() => setDemandCategory(category.value)}
                              className="w-4 h-4 focus:ring-2"
                              style={{ accentColor: colors.primary }}
                            />
                            <span
                              className="text-sm transition-colors"
                              style={{ color: colors.textSecondary }}
                            >
                              {category.label}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
              </>
            )}
            {activeSubTab !== "accountability" ? (
              /* Volume Metrics (Intake, Supply, Demand) */
              <>
                <div
                  className="border-b pb-3 mb-4"
                  style={{ borderColor: colors.border }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      Account
                    </span>
                    <span
                      className="text-xs font-medium"
                      style={{ color: colors.textSecondary }}
                    >
                      Gallons
                    </span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm font-bold"
                      style={{ color: colors.text }}
                    >
                      Total
                    </span>
                    <span
                      className="text-lg font-bold"
                      style={{ color: colors.primary }}
                    >
                      {apiStats.total
                        ? Number(apiStats.total).toLocaleString()
                        : 0}
                    </span>
                  </div>
                  <div
                    className="h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        backgroundImage:
                          colors.gradient ||
                          `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                {/* Account Level Breakdown */}
                {apiStats.account_flow?.map((flow, index) => {
                  const totalVal = apiStats.total > 0 ? apiStats.total : 1;
                  const percentage = (Number(flow.sum) / totalVal) * 100;
                  const barColor =
                    resolvedSystem === "non_potable"
                      ? "#A855F7" // Solid Purple for all items in Non-Potable mode
                      : areaColors[index % areaColors.length];
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2 mt-4">
                        <span
                          className="text-sm font-medium w-full"
                          title={flow.account_name}
                          style={{ color: colors.textSecondary }}
                        >
                          {flow.account_name}
                        </span>
                        <span
                          className="text-base font-bold"
                          style={{ color: colors.primary }}
                        >
                          {flow.sum ? Number(flow.sum).toLocaleString() : 0}
                        </span>
                      </div>
                      <div
                        className="h-1 rounded-full overflow-hidden"
                        style={{ backgroundColor: `${colors.primary}20` }}
                      >
                        <div
                          className="h-full transition-all duration-500"
                          style={{
                            backgroundColor:
                              areaColors[index % areaColors.length],
                            width: `${percentage}%`,
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-2">
                    <span
                      className="text-sm font-bold"
                      style={{ color: colors.text }}
                    >
                      Total Supply
                    </span>
                    <span
                      className="text-lg font-bold tabular-nums"
                      style={{ color: colors.primary }}
                    >
                      {Number(apiStats.supply_total || 0).toLocaleString()}
                    </span>
                  </div>
                  <div
                    className="h-1 rounded-full overflow-hidden"
                    style={{ backgroundColor: `${colors.primary}20` }}
                  >
                    <div
                      className="h-full transition-all duration-500"
                      style={{
                        backgroundImage:
                          colors.gradient ||
                          `linear-gradient(to right, ${colors.primary}, ${colors.secondary})`,
                        width: "100%",
                      }}
                    />
                  </div>
                </div>

                {(activeSubTab === "accountability"
                  ? [
                      {
                        label: "Accountability",
                        avg: apiStats.average,
                        max: apiStats.maximum,
                        min: apiStats.minimum,
                        demandTotal: apiStats.demand_total,
                        totalAccountability: apiStats.accountability_percentage,
                        show: true, // Always show Base
                      },
                      {
                        // label: "Acct + Maint + Temp",
                        label: "Acct -Maint",
                        avg: apiStats.average_total,
                        max: apiStats.maximum_total,
                        min: apiStats.minimum_total,
                        demandTotal: apiStats.demand_total_total,
                        totalAccountability: apiStats.percentage_total,
                        show: showMaintenance, // Correctly hides for ALL districts when false
                      },
                      {
                        // label: "Maint + Temp",
                        label: "Maintenance",
                        avg: apiStats.average_maint_temp,
                        max: apiStats.maximum_maint_temp,
                        min: apiStats.minimum_maint_temp,
                        demandTotal: apiStats.demand_total_maint_temp,
                        totalAccountability: 0,
                        show: showMaintenance, // Correctly hides for ALL districts when false
                      },
                      
                    ].filter((group) => group.show) // This filter ensures "Basic" mode stays basic
                  : []
                ).map((group) => (
                  <div
                    key={group.label}
                    className="space-y-4 border-b pb-4 last:border-0"
                    style={{ borderColor: colors.border }}
                  >
                    <h4
                      className="text-xs font-bold"
                      style={{ color: colors.primary }}
                    >
                      {group.label}
                    </h4>
                    <div className="flex items-center justify-between mb-1">
                      <span style={{ color: colors.textSecondary }} className="text-xs">
                        Total Accountability
                      </span>
                      <span
                        className="text-sm font-bold tabular-nums"
                        style={{ color: colors.text }}
                      >
                        {group.totalAccountability}%
                      </span>
                    </div>
                    {/* Progress Bars for Avg, Max, Min */}
                    {[
                      { type: "Avg", val: group.avg },
                      { type: "Max", val: group.max },
                      { type: "Min", val: group.min },
                    ].map((stat) => (
                      <div key={stat.type}>
                        <div className="flex items-center justify-between mb-1">
                          <span
                            className="text-xs"
                            style={{ color: colors.textSecondary }}
                          >
                            {stat.type}
                          </span>
                          <span
                            className="text-sm font-bold tabular-nums"
                            style={{ color: colors.text }}
                          >
                            {stat.val}%
                          </span>
                        </div>
                        <div
                          className="h-1 rounded-full overflow-hidden"
                          style={{ backgroundColor: `${colors.primary}20` }}
                        >
                          <div
                            className="h-full transition-all duration-500"
                            style={{
                              backgroundColor:
                                group.label === "Maintenance"
                                  ? "#f97316"
                                  : group.label === "Acct + Maint"
                                    ? "#10b981"
                                    : colors.primary,
                              width: `${Math.min(100, Math.max(0, stat.val))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    {/* Total Accountability Row (Optional extra display) */}

                    {/* NEW: Total Volumes (Demand / Supply) at the bottom of the card */}
                    {(group.demandTotal !== undefined ||
                      group.supplyTotal !== undefined) && (
                      <div
                        className="mt-3 pt-3 border-t border-dashed space-y-1.5"
                        style={{ borderColor: `${colors.border}80` }}
                      >
                        {group.demandTotal !== undefined && (
                          <div className="flex items-center justify-between text-[11px]">
                            <span style={{ color: colors.textSecondary }}>
                              Total Demand
                            </span>
                            <span
                              className="font-semibold tabular-nums"
                              style={{ color: colors.text }}
                            >
                              {Number(group.demandTotal).toLocaleString()}
                            </span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
